import { z } from "zod";
import type { ApplicationRole, AuthenticatedUser } from "@yazamco/shared-types";

/**
 * The application roles, ordered from least to most privileged. The array order
 * defines the hierarchy used by the authorization helpers.
 */
export const APPLICATION_ROLES = [
  "Viewer",
  "Operator",
  "Administrator",
] as const satisfies readonly ApplicationRole[];

/** Shared Zod schema for a single application role. */
export const applicationRoleSchema = z.enum(APPLICATION_ROLES);

/** Numeric rank per role: Viewer=0 < Operator=1 < Administrator=2. */
const ROLE_RANK: Record<ApplicationRole, number> = {
  Viewer: 0,
  Operator: 1,
  Administrator: 2,
};

/**
 * True when the user holds at least the required role (hierarchical).
 * - Viewer requirement  → Viewer, Operator, Administrator
 * - Operator requirement → Operator, Administrator
 * - Administrator req.   → Administrator only
 */
export function satisfiesRole(
  userRoles: readonly ApplicationRole[],
  required: ApplicationRole,
): boolean {
  const requiredRank = ROLE_RANK[required];
  return userRoles.some((role) => ROLE_RANK[role] >= requiredRank);
}

/**
 * Role gate for an authenticated user. Super Administrator is an explicit
 * authority override and therefore satisfies every present and future
 * hierarchical role requirement.
 */
export function hasRoleAccess(
  user: Pick<AuthenticatedUser, "roles" | "isSuperAdmin">,
  required: ApplicationRole,
): boolean {
  return user.isSuperAdmin === true || satisfiesRole(user.roles, required);
}
